import { CheckCircle, AlertTriangle, XCircle, Download } from 'lucide-react';
import type { BulkUploadResult, CsvEntityConfig } from '../types';

interface BulkUploadResultsProps {
  result: BulkUploadResult;
  config: CsvEntityConfig;
}

export function BulkUploadResults({ result, config }: BulkUploadResultsProps) {
  const downloadErrorReport = () => {
    if (result.errors.length === 0) return;
    const headers = 'row,field,message';
    const lines = result.errors.map(e =>
      `${e.row},"${e.field || ''}","${e.message.replace(/"/g, '""')}"`
    );
    const csv = [headers, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${config.entityName.toLowerCase().replace(/\s+/g, '-')}-errors.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <div>
            <div className="text-lg font-bold text-green-700 dark:text-green-400">{result.created}</div>
            <div className="text-xs text-green-600 dark:text-green-500">Created</div>
          </div>
        </div>
        <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <AlertTriangle className="w-5 h-5 text-yellow-600" />
          <div>
            <div className="text-lg font-bold text-yellow-700 dark:text-yellow-400">{result.skipped}</div>
            <div className="text-xs text-yellow-600 dark:text-yellow-500">Skipped</div>
          </div>
        </div>
        <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
          <div>
            <div className="text-lg font-bold text-gray-700 dark:text-gray-300">{result.total}</div>
            <div className="text-xs text-gray-500">Total Rows</div>
          </div>
        </div>
      </div>

      {result.errors.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-red-700 dark:text-red-400 flex items-center gap-1">
              <XCircle className="w-4 h-4" />
              Errors ({result.errors.length})
            </h4>
            <button
              onClick={downloadErrorReport}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <Download className="w-3 h-3" />
              Download Error Report
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto border border-red-200 dark:border-red-800 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-red-50 dark:bg-red-900/30 sticky top-0">
                <tr>
                  <th className="px-3 py-1.5 text-left font-medium text-red-700 dark:text-red-400">Row</th>
                  <th className="px-3 py-1.5 text-left font-medium text-red-700 dark:text-red-400">Field</th>
                  <th className="px-3 py-1.5 text-left font-medium text-red-700 dark:text-red-400">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-100 dark:divide-red-800">
                {result.errors.map((err, idx) => (
                  <tr key={idx} className="bg-white dark:bg-gray-800">
                    <td className="px-3 py-1 text-gray-600 dark:text-gray-400">{err.row}</td>
                    <td className="px-3 py-1 text-gray-600 dark:text-gray-400">{err.field || '-'}</td>
                    <td className="px-3 py-1 text-red-600 dark:text-red-400">{err.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}



